import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { FadeUp } from "@/components/motion/Reveal";
import { createLead } from "@/lib/api";

export default function FinalCTA() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await createLead(form);
      setDone(true);
      toast.success("Welcome to the Advanced Life Policies family! Check your inbox.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="signup" className="bg-cream py-24 lg:py-32" data-testid="final-cta-section">
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-forest text-cream px-8 py-14 lg:px-16 lg:py-20 grain">
          <div className="pointer-events-none absolute -top-24 -right-16 h-80 w-80 rounded-full bg-terracotta/20 blur-3xl" />

          <div className="relative z-10 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <FadeUp>
                <span className="text-xs tracking-[0.2em] uppercase font-bold text-sage-light">
                  Save &amp; protect your people
                </span>
                <h2 className="mt-4 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight">
                  Ready to save &amp; secure your future?
                </h2>
                <p className="mt-5 text-cream/70 max-w-md leading-relaxed">
                  Start with a free 60-second quote. No calls, no pressure — just an honest number
                  and a licensed advisor who&apos;s got your back.
                </p>
              </FadeUp>

              {!done ? (
                <FadeUp delay={0.1}>
                  <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg" data-testid="cta-form">
                    <Input
                      placeholder="First name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="h-14 rounded-full bg-cream/95 border-transparent text-forest placeholder:text-forest/40 px-6"
                      data-testid="cta-name"
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="h-14 rounded-full bg-cream/95 border-transparent text-forest placeholder:text-forest/40 px-6"
                      data-testid="cta-email"
                    />
                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="cta-submit"
                      className="h-14 rounded-full bg-terracotta hover:bg-terracotta-dark text-cream px-8 font-semibold whitespace-nowrap transition-all duration-300 hover:-translate-y-0.5 active:scale-95"
                    >
                      {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Get started"}
                    </Button>
                  </form>
                </FadeUp>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 inline-flex items-center gap-3 rounded-full bg-sage/20 border border-sage/40 px-6 py-4"
                  data-testid="cta-success"
                >
                  <CheckCircle2 className="h-6 w-6 text-sage-light" />
                  <span className="text-cream">You&apos;re on the list — Advanced Life Policies will be in touch shortly!</span>
                </motion.div>
              )}

              <button
                onClick={() => navigate("/apply")}
                className="mt-5 text-sm text-cream/60 hover:text-cream underline underline-offset-4 transition-colors"
                data-testid="cta-apply-link"
              >
                Ready? Start your full application →
              </button>
            </div>

            <FadeUp delay={0.15} className="lg:col-span-5">
              <div className="relative rounded-2xl border border-cream/15 bg-forest-700/40 p-10 text-cream/80" data-testid="cta-side-card">
                <p className="text-[11px] uppercase tracking-[0.24em] text-cream/45">Our promise</p>
                <p className="mt-4 font-serif text-3xl leading-tight text-cream">
                  A licensed advisor, on the phone, within one business day.
                </p>
                <div className="mt-8 pt-8 border-t border-cream/10 grid grid-cols-2 gap-6 text-sm">
                  <div>
                    <p className="font-serif text-3xl text-cream">1:1</p>
                    <p className="text-cream/50 mt-1">Real people, no call centre</p>
                  </div>
                  <div>
                    <p className="font-serif text-3xl text-cream">$0</p>
                    <p className="text-cream/50 mt-1">To find out what you&apos;d save</p>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}
